import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  createVoucherInput,
  FROZEN_VOUCHER_FIELDS,
  voucherKind,
  voucherIssues,
  voucherState,
  type AdminFormat,
  type AdminVoucher,
  type CreateVoucherInput,
  type UpdateVoucherInput,
} from '@gg/shared';
import {
  Badge,
  Button,
  Card,
  CheckboxField,
  ConfirmDialog,
  LockIcon,
  Notice,
  Recap,
  RecapRow,
  SelectField,
  Table,
  Td,
  TextField,
  Th,
} from '@/components/ui';
import { adminApiErrorKey, adminIssueKey } from '@/lib/adminErrors';
import { voucherStateTone } from '@/lib/adminTones';
import { ApiRequestError } from '@/lib/api';
import { eventLocalToIso, formatDateTime, isoToEventLocal } from '@/lib/dates';
import i18n, { centsToEuroInput, formatPrice, parseEurosToCents } from '@/lib/i18n';
import {
  useAdminFormats,
  useAdminVouchers,
  useCreateVoucher,
  useDeleteVoucher,
  useUpdateVoucher,
} from '@/lib/queries';

type Values = {
  code: string;
  kind: 'percent' | 'fixed';
  value: string;
  maxUses: string;
  validFrom: string;
  validUntil: string;
  formatIds: string[];
};
type Field = keyof Values;
type Pending = { title: string; lines: string[]; confirm: string; run: () => Promise<unknown> };

const intOrNull = (s: string) =>
  s.trim() === '' ? null : /^\d+$/.test(s.trim()) ? Number(s) : NaN;

const toValues = (v: AdminVoucher | null): Values => ({
  code: v?.code ?? '',
  kind: v?.kind ?? 'percent',
  value: !v ? '' : v.kind === 'fixed' ? centsToEuroInput(v.value) : String(v.value),
  maxUses: v?.maxUses ? String(v.maxUses) : '',
  validFrom: isoToEventLocal(v?.validFrom ?? null),
  validUntil: isoToEventLocal(v?.validUntil ?? null),
  formatIds: v?.formatIds ?? [],
});

const toInput = (v: Values) => ({
  code: v.code,
  kind: v.kind,
  value: v.kind === 'fixed' ? (parseEurosToCents(v.value) ?? NaN) : (intOrNull(v.value) ?? NaN),
  maxUses: intOrNull(v.maxUses),
  validFrom: v.validFrom ? eventLocalToIso(v.validFrom) : null,
  validUntil: v.validUntil ? eventLocalToIso(v.validUntil) : null,
  formatIds: v.formatIds,
});

/** "−10 %" or "−15 €". */
const discount = (v: Pick<AdminVoucher, 'kind' | 'value'>) =>
  v.kind === 'percent'
    ? `−${new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(v.value / 100)}`
    : `−${formatPrice(v.value)}`;

function validity(t: TFunction, v: Pick<AdminVoucher, 'validFrom' | 'validUntil'>) {
  const from = v.validFrom && formatDateTime(v.validFrom);
  const until = v.validUntil && formatDateTime(v.validUntil);
  if (from && until) return t('admin.tiers.window.both', { from, until });
  if (from) return t('admin.tiers.window.from', { from });
  if (until) return t('admin.tiers.window.until', { until });
  return t('admin.tiers.window.always');
}

function formatNames(t: TFunction, ids: string[], formats: AdminFormat[]) {
  if (!ids.length) return t('admin.vouchers.allFormats');
  return ids
    .map((id) => formats.find((f) => f.id === id)?.name ?? t('admin.vouchers.deletedFormat'))
    .join(', ');
}

/**
 * A-02: voucher codes. Checkout reads them live, so every change applies to the next code
 * check. A code an order uses is frozen: only its max uses and dates move ("End now").
 */
export function AdminVouchers() {
  const { t } = useTranslation();
  const list = useAdminVouchers();
  const formats = useAdminFormats().data?.formats ?? [];
  const update = useUpdateVoucher();
  const remove = useDeleteVoucher();
  const [editing, setEditing] = useState<AdminVoucher | 'new' | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const now = new Date();

  const run = async () => {
    if (!pending) return;
    setError(null);
    try {
      await pending.run();
    } catch (e) {
      setError(apiMessage(t, e));
    } finally {
      setPending(null);
    }
  };

  const askEnd = (v: AdminVoucher) =>
    setPending({
      title: t('admin.vouchers.end.title'),
      confirm: t('admin.vouchers.end.confirm'),
      lines: [t('admin.vouchers.end.body', { code: v.code })],
      run: () => update.mutateAsync({ id: v.id, body: { validUntil: new Date().toISOString() } }),
    });

  const askDelete = (v: AdminVoucher) =>
    setPending({
      title: t('admin.vouchers.delete.title'),
      confirm: t('admin.vouchers.delete.confirm'),
      lines: [t('admin.vouchers.delete.body', { code: v.code })],
      run: () => remove.mutateAsync(v.id),
    });

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-display text-section-lg font-bold">{t('admin.vouchers.title')}</h1>
        {!editing && (
          <Button size="md" className="w-auto!" onClick={() => setEditing('new')}>
            {t('admin.vouchers.new')}
          </Button>
        )}
      </div>
      <p className="text-sm text-ink-muted">{t('admin.vouchers.intro')}</p>

      {editing && (
        <VoucherForm
          key={editing === 'new' ? 'new' : editing.id}
          voucher={editing === 'new' ? null : editing}
          formats={formats}
          onDone={() => setEditing(null)}
        />
      )}

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {list.isError && (
        <p role="alert" className="text-sm text-danger">
          {t('admin.vouchers.error')}
        </p>
      )}
      {list.data &&
        (list.data.vouchers.length === 0 ? (
          <p className="py-8 text-center text-ink-muted">{t('admin.vouchers.empty')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('admin.vouchers.col.code')}</Th>
                <Th className="text-right">{t('admin.vouchers.col.discount')}</Th>
                <Th>{t('admin.vouchers.col.formats')}</Th>
                <Th>{t('admin.vouchers.col.validity')}</Th>
                <Th className="text-right">{t('admin.vouchers.col.uses')}</Th>
                <Th>{t('admin.vouchers.col.state')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {list.data.vouchers.map((v) => {
                const state = voucherState(v, now);
                const frozen = v.orders > 0;
                return (
                  <tr key={v.id}>
                    <Td>
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-mono font-semibold">{v.code}</span>
                        {frozen && (
                          <span title={t('admin.vouchers.frozenHint', { count: v.orders })}>
                            <Badge>
                              <LockIcon size={12} className="mr-1" />
                              {t('admin.vouchers.frozen')}
                            </Badge>
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap text-right font-semibold">{discount(v)}</Td>
                    <Td>{formatNames(t, v.formatIds, formats)}</Td>
                    <Td className="text-ink-muted">{validity(t, v)}</Td>
                    <Td className="whitespace-nowrap text-right">
                      {v.maxUses === null
                        ? t('admin.vouchers.usesUnlimited', { uses: v.uses })
                        : t('admin.vouchers.uses', { uses: v.uses, max: v.maxUses })}
                      {v.held > 0 && (
                        <span className="block text-helper text-ink-muted">
                          {t('admin.vouchers.held', { count: v.held })}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={voucherStateTone(state)}>
                        {t(`admin.voucherState.${state}`)}
                      </Badge>
                    </Td>
                    <Td className="whitespace-nowrap text-right">
                      {!editing && (
                        <span className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="cursor-pointer font-semibold text-accent"
                            onClick={() => setEditing(v)}
                          >
                            {t('admin.vouchers.edit')}
                          </button>
                          {state !== 'ended' && state !== 'scheduled' && (
                            <button
                              type="button"
                              className="cursor-pointer font-semibold text-accent"
                              onClick={() => askEnd(v)}
                            >
                              {t('admin.vouchers.end.action')}
                            </button>
                          )}
                          {!frozen && (
                            <button
                              type="button"
                              className="cursor-pointer text-ink-muted"
                              onClick={() => askDelete(v)}
                            >
                              {t('admin.vouchers.delete.action')}
                            </button>
                          )}
                        </span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ))}

      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ''}
        confirmLabel={pending?.confirm ?? ''}
        cancelLabel={t('admin.confirm.cancel')}
        loading={update.isPending || remove.isPending}
        onConfirm={run}
        onCancel={() => setPending(null)}
      >
        {pending?.lines.map((l) => (
          <p key={l} className="m-0">
            {l}
          </p>
        ))}
      </ConfirmDialog>
    </section>
  );
}

function apiMessage(t: TFunction, e: unknown) {
  const details = (e instanceof ApiRequestError ? e.details : null) as {
    uses?: number;
    code?: string;
  } | null;
  return t(`admin.error.${adminApiErrorKey(e)}`, { uses: details?.uses, code: details?.code });
}

function VoucherForm({
  voucher,
  formats,
  onDone,
}: {
  voucher: AdminVoucher | null;
  formats: AdminFormat[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateVoucher();
  const update = useUpdateVoucher();
  const frozen = (voucher?.orders ?? 0) > 0;
  const { register, handleSubmit, setError, watch, formState } = useForm<Values>({
    defaultValues: toValues(voucher),
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ patch: UpdateVoucherInput; lines: string[] } | null>(
    null,
  );
  const kind = watch('kind');
  const err = (f: Field) => formState.errors[f]?.message;

  const onSubmit = async (typed: Values) => {
    // A used voucher shows no inputs for its code, value and formats: it keeps its own.
    const v = frozen && voucher ? { ...toValues(voucher), ...pickDates(typed) } : typed;
    setApiError(null);
    setNotice(null);
    const parsed = createVoucherInput.safeParse(toInput(v));
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as Field;
        if (field in v) setError(field, { message: t(`admin.error.${adminIssueKey(issue)}`) });
      }
      // Zod skips the cross-field rules while a field is invalid: show them at the same time.
      for (const issue of voucherIssues(toInput(v)))
        setError(issue.path, { message: t(`admin.error.${issue.message}`) });
      return;
    }
    if (!voucher) {
      try {
        await create.mutateAsync(parsed.data);
        onDone();
      } catch (e) {
        showError(e);
      }
      return;
    }
    const patch = changedFields(voucher, parsed.data, frozen);
    if (!Object.keys(patch).length) return setNotice(t('admin.vouchers.form.nothing'));
    // An edit applies to the next code check: say what changes before saving (decision 0018 D7).
    setPending({ patch, lines: changeLines(t, voucher, patch, formats) });
  };

  const save = async (patch: UpdateVoucherInput) => {
    try {
      await update.mutateAsync({ id: voucher!.id, body: patch });
      onDone();
    } catch (e) {
      showError(e);
    } finally {
      setPending(null);
    }
  };

  const showError = (e: unknown) => {
    const key = adminApiErrorKey(e);
    const message = apiMessage(t, e);
    if (key === 'code_taken') setError('code', { message });
    else if (key === 'max_uses_below_uses') setError('maxUses', { message });
    else setApiError(message);
  };

  // Editable on every voucher, used or not.
  const limits = (
    <>
      <TextField
        label={t('admin.vouchers.form.maxUses')}
        hint={
          voucher
            ? t('admin.vouchers.form.maxUsesHintUsed', { uses: voucher.uses })
            : t('admin.vouchers.form.maxUsesHint')
        }
        inputMode="numeric"
        error={err('maxUses')}
        {...register('maxUses')}
      />
      <TextField
        type="datetime-local"
        label={t('admin.vouchers.form.validFrom')}
        hint={t('admin.tiers.form.openHint')}
        error={err('validFrom')}
        {...register('validFrom')}
      />
      <TextField
        type="datetime-local"
        label={t('admin.vouchers.form.validUntil')}
        hint={t('admin.tiers.form.openHint')}
        error={err('validUntil')}
        {...register('validUntil')}
      />
    </>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card className="gap-4!">
        <h2 className="text-lg font-bold">
          {voucher
            ? t('admin.vouchers.form.editTitle', { code: voucher.code })
            : t('admin.vouchers.form.addTitle')}
        </h2>
        {frozen && voucher ? (
          <>
            <Notice
              icon={<LockIcon />}
              title={t('admin.vouchers.form.lockedTitle', { count: voucher.orders })}
            >
              {t('admin.vouchers.form.lockedBody')}
            </Notice>
            <Recap aria-label={t('admin.vouchers.form.lockedTitle', { count: voucher.orders })}>
              <RecapRow label={t('admin.vouchers.form.code')} value={voucher.code} />
              <RecapRow label={t('admin.vouchers.col.discount')} value={discount(voucher)} />
              <RecapRow
                label={t('admin.vouchers.form.formats')}
                value={formatNames(t, voucher.formatIds, formats)}
              />
            </Recap>
            <h3 className="m-0 text-base font-bold">{t('admin.vouchers.form.editable')}</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{limits}</div>
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <TextField
                label={t('admin.vouchers.form.code')}
                hint={t('admin.vouchers.form.codeHint')}
                autoCapitalize="characters"
                error={err('code')}
                {...register('code')}
              />
              <SelectField label={t('admin.vouchers.form.kind')} {...register('kind')}>
                {voucherKind.options.map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.vouchers.kind.${k}`)}
                  </option>
                ))}
              </SelectField>
              <TextField
                label={t(`admin.vouchers.form.value.${kind}`)}
                inputMode={kind === 'fixed' ? 'decimal' : 'numeric'}
                error={err('value')}
                {...register('value')}
              />
              {limits}
            </div>
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="mb-2 text-sm font-semibold">
                {t('admin.vouchers.form.formats')}
              </legend>
              <span className="text-helper text-ink-muted">
                {t('admin.vouchers.form.formatsHint')}
              </span>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {formats.map((f) => (
                  <CheckboxField key={f.id} value={f.id} {...register('formatIds')}>
                    {f.name}
                  </CheckboxField>
                ))}
              </div>
            </fieldset>
          </>
        )}
        <div className="flex items-center gap-4">
          <Button
            type="submit"
            size="md"
            className="w-auto!"
            loading={create.isPending || update.isPending}
          >
            {t(voucher ? 'admin.vouchers.form.save' : 'admin.vouchers.form.create')}
          </Button>
          <Button variant="ghost" size="md" className="w-auto!" onClick={onDone}>
            {t('admin.confirm.cancel')}
          </Button>
          {notice && (
            <span role="status" className="text-sm text-ink-muted">
              {notice}
            </span>
          )}
          {apiError && (
            <span role="alert" className="text-sm text-danger">
              {apiError}
            </span>
          )}
        </div>
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={t('admin.vouchers.confirmEdit.title', { code: voucher?.code })}
        confirmLabel={t('admin.confirm.apply')}
        cancelLabel={t('admin.confirm.cancel')}
        loading={update.isPending}
        onConfirm={() => pending && save(pending.patch)}
        onCancel={() => setPending(null)}
      >
        <div className="flex flex-col gap-3">
          <p className="m-0">{t('admin.vouchers.confirmEdit.live')}</p>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-sm">
            {pending?.lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </ConfirmDialog>
    </form>
  );
}

/** "Discount: −10% → −15 €", one line per changed field, for the edit confirmation. */
function changeLines(
  t: TFunction,
  v: AdminVoucher,
  patch: UpdateVoucherInput,
  formats: AdminFormat[],
): string[] {
  const line = (field: string, before: string, after: string) =>
    t('admin.vouchers.confirmEdit.line', { field: t(field), before, after });
  const limit = (n: number | null) =>
    n === null ? t('admin.vouchers.confirmEdit.noLimit') : String(n);
  const when = (d: string | null) =>
    d ? formatDateTime(d) : t('admin.vouchers.confirmEdit.noLimit');
  const lines: string[] = [];
  if (patch.code !== undefined) lines.push(line('admin.vouchers.form.code', v.code, patch.code));
  if (patch.kind !== undefined || patch.value !== undefined) {
    const next = { kind: patch.kind ?? v.kind, value: patch.value ?? v.value };
    lines.push(line('admin.vouchers.col.discount', discount(v), discount(next)));
  }
  if (patch.formatIds !== undefined)
    lines.push(
      line(
        'admin.vouchers.form.formats',
        formatNames(t, v.formatIds, formats),
        formatNames(t, patch.formatIds, formats),
      ),
    );
  if (patch.maxUses !== undefined)
    lines.push(line('admin.vouchers.form.maxUses', limit(v.maxUses), limit(patch.maxUses)));
  if (patch.validFrom !== undefined)
    lines.push(line('admin.vouchers.form.validFrom', when(v.validFrom), when(patch.validFrom)));
  if (patch.validUntil !== undefined)
    lines.push(line('admin.vouchers.form.validUntil', when(v.validUntil), when(patch.validUntil)));
  return lines;
}

const pickDates = (v: Values) => ({
  maxUses: v.maxUses,
  validFrom: v.validFrom,
  validUntil: v.validUntil,
});

/**
 * The patch: changed fields only. A frozen voucher never sends its frozen fields (the form
 * doesn't show them, and a format deleted since stays in its list untouched).
 */
function changedFields(
  v: AdminVoucher,
  next: CreateVoucherInput,
  frozen: boolean,
): UpdateVoucherInput {
  const same = (k: keyof CreateVoucherInput) => {
    if (k === 'formatIds')
      return [...v.formatIds].sort().join() === [...next.formatIds].sort().join();
    if (k === 'validFrom' || k === 'validUntil') {
      const a = v[k] && new Date(v[k]).getTime();
      const b = next[k] && new Date(next[k]).getTime();
      return a === b;
    }
    return v[k] === next[k];
  };
  const keys = (Object.keys(next) as (keyof CreateVoucherInput)[]).filter(
    (k) => !(frozen && (FROZEN_VOUCHER_FIELDS as readonly string[]).includes(k)) && !same(k),
  );
  return Object.fromEntries(keys.map((k) => [k, next[k]])) as UpdateVoucherInput;
}
